/**
 * ChannelPackagePreviewModal Component
 *
 * Modal zur Vorschau eines Channel Packages.
 * Zeigt alle Bilder, Content und Metadata an.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/v2/ui/Dialog';
import { Button } from '@/components/v2/ui';
import { getAssetThumbnailUrl } from '@/lib/helpers/asset-urls';
import type { ChannelPackage, AssetRendition } from '@/types/channel-packages';
import { X, ZoomIn } from 'lucide-react';

interface ChannelPackagePreviewModalProps {
  package_: ChannelPackage | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ChannelPackagePreviewModal({
  package_: pkg,
  isOpen,
  onClose,
}: ChannelPackagePreviewModalProps) {
  const { t } = useTranslation();
  const [lightboxImage, setLightboxImage] = useState<AssetRendition | null>(null);

  if (!pkg) return null;

  const version = pkg.current_version;
  const content = version?.content;
  const assetRenditions = version?.asset_renditions || [];

  return (
    <>
      {/* Main Preview Dialog */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Channel Package Preview
            </DialogTitle>
            <DialogDescription>
              {pkg.channel?.name || t('channelPackages.unknownChannel')} ({pkg.channel?.channel_type})
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status Info */}
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <span>Version {version?.version_number || 'N/A'}</span>
              <span>•</span>
              <span>Status: {version?.status || 'N/A'}</span>
              <span>•</span>
              <span>{t('channelPackages.imagesCount_other', { count: assetRenditions.length })}</span>
            </div>

            {/* Asset Renditions (Images) */}
            {assetRenditions.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-3">
                  {t('channelPackages.imagesSection')}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {assetRenditions.map((rendition, idx) => (
                    <div
                      key={rendition.asset_id || idx}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-neutral-200 bg-neutral-100"
                    >
                      <img
                        src={getAssetThumbnailUrl(rendition)}
                        alt={rendition.alt_text || t('channelPackages.imageSlotAlt', { slot: rendition.slot })}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />

                      {/* Slot Badge */}
                      <div className="absolute top-2 left-2 bg-black/75 text-white px-2 py-0.5 rounded text-xs font-semibold">
                        #{rendition.slot}
                      </div>

                      {/* Zoom Button */}
                      <button
                        onClick={() => setLightboxImage(rendition)}
                        className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100"
                        aria-label={t('gallery.enlarge')}
                      >
                        <ZoomIn className="w-8 h-8 text-white" />
                      </button>

                      {/* Alt Text */}
                      {rendition.alt_text && (
                        <div className="absolute bottom-0 inset-x-0 bg-black/75 text-white p-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                          {rendition.alt_text}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            {content && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-neutral-900">
                  Content
                </h3>

                {/* Title */}
                {content.title && (
                  <div>
                    <label className="text-xs font-medium text-neutral-700">
                      {t('channelPackages.titleLabel')}
                    </label>
                    <p className="text-sm text-neutral-900 mt-1">
                      {content.title}
                    </p>
                  </div>
                )}

                {/* Description */}
                {content.description && (
                  <div>
                    <label className="text-xs font-medium text-neutral-700">
                      {t('channelPackages.descriptionLabel')}
                    </label>
                    <p className="text-sm text-neutral-900 mt-1 whitespace-pre-wrap">
                      {content.description}
                    </p>
                  </div>
                )}

                {/* Short Description */}
                {content.short_description && (
                  <div>
                    <label className="text-xs font-medium text-neutral-700">
                      {t('channelPackages.shortDescriptionLabel')}
                    </label>
                    <p className="text-sm text-neutral-900 mt-1">
                      {content.short_description}
                    </p>
                  </div>
                )}

                {/* Bullets */}
                {content.bullets && content.bullets.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-neutral-700">
                      Bullet Points
                    </label>
                    <ul className="mt-1 space-y-1">
                      {content.bullets.map((bullet, idx) => (
                        <li key={idx} className="text-sm text-neutral-900 flex items-start gap-2">
                          <span className="text-neutral-400">•</span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* SEO Meta */}
                {content.seo_meta && (
                  <div>
                    <label className="text-xs font-medium text-neutral-700">
                      {t('channelPackages.seoMetadataLabel')}
                    </label>
                    <div className="mt-2 space-y-2 text-sm">
                      {content.seo_meta.title && (
                        <div>
                          <span className="font-medium">Title:</span> {content.seo_meta.title}
                        </div>
                      )}
                      {content.seo_meta.description && (
                        <div>
                          <span className="font-medium">Description:</span> {content.seo_meta.description}
                        </div>
                      )}
                      {content.seo_meta.keywords && (
                        <div>
                          <span className="font-medium">Keywords:</span> {content.seo_meta.keywords.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Metadata */}
            {version?.metadata && Object.keys(version.metadata).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-neutral-900 mb-2">
                  Metadata
                </h3>
                <pre className="text-xs bg-neutral-100 p-3 rounded overflow-x-auto">
                  {JSON.stringify(version.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox for Images */}
      {lightboxImage && (
        <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
          <DialogContent className="max-w-6xl p-0 bg-black border-0" showCloseButton={false}>
            {/* Close Button */}
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-2 transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-6 h-6" />
            </button>

            {/* Image */}
            <div className="relative w-full" style={{ maxHeight: '90vh' }}>
              <img
                src={lightboxImage.s3_key_original ? `${lightboxImage.s3_key_original}` : getAssetThumbnailUrl(lightboxImage)}
                alt={lightboxImage.alt_text || t('channelPackages.imageSlotAlt', { slot: lightboxImage.slot })}
                className="w-full h-auto max-h-[90vh] object-contain"
              />

              {/* Image Info Overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 to-transparent p-6">
                <div className="text-white">
                  <div className="text-lg font-semibold">
                    Slot #{lightboxImage.slot}
                  </div>
                  {lightboxImage.alt_text && (
                    <div className="text-sm text-white/80 mt-1">
                      {lightboxImage.alt_text}
                    </div>
                  )}
                  <div className="flex gap-4 text-xs text-white/60 mt-2">
                    {lightboxImage.width && lightboxImage.height && (
                      <span>{lightboxImage.width} × {lightboxImage.height} px</span>
                    )}
                    {lightboxImage.mime_type && (
                      <span>{lightboxImage.mime_type}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
